import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { pluralize } from '../../utils';
import { buildSearchResults, splitOnSearchTerm } from '../../utils/json-search';
import Button from './base/Button';
import Text from './base/Text';
import DropdownMenu, { DropdownMenuOption } from './DropdownMenu';
import { Div } from './helpers/StyledUtils';
import { useCopyToClipboard } from '../../utils/copy';
import { FiltersContext } from './Filters/Filters';
import { StyledDropdownWrapper } from './Dropdown';
import { useToasts } from './Toast';
import { TableContext } from '../contexts/TableContext';
import Icon from './base/Icon';
import Tooltip from './base/Tooltip';

/** Config for JSON browser context menu. Defined by the parent so the browser stays generic. */
export interface ContextMenuConfig {
  /** Key in filters_context.values to read/write for "Add to filter" / "Remove from filter". */
  filter_field_key?: string;
  /** Prefix for table column paths (e.g. "body", "headers") for "Add to table" / "Remove from table". */
  table_column_prefix?: string;
}
export function insertPathEllipsis(text: string, max_length: number) {
  if (text.length <= max_length) return text;
  const ellipsis_length = 3;
  const initial_length = 1;
  const side_length = max_length - ellipsis_length - initial_length;
  return text.slice(0, initial_length) + '...' + text.slice(text.length - side_length);
}
type JSONPrimitive = string | number | boolean | null;
export type JSONValue = JSONPrimitive | (JSONPrimitive | JSONValue)[] | {
  [key: string]: JSONPrimitive | JSONValue;
};
const isPrimitive = (value: JSONValue) => {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null;
};
export const DynamicColumnHeader: React.FC<{
  full_path: string;
  width?: number;
}> = ({
  full_path,
  width = 200
}) => {
  const filters_context = useContext(FiltersContext);
  const table_context = useContext(TableContext);
  const [type, ...path] = full_path.split('.');
  const field_type = type === 'body' ? 'request_body' : 'request_headers';

  // Check if there's an active filter for this field
  let filtered_value = null;
  try {
    const current_filter = JSON.parse(filters_context.values?.[field_type] || '{}');
    filtered_value = getNestedValue(current_filter, path);
  } catch (error) {
    // No filter or invalid JSON
  }
  const is_filtered = filtered_value !== undefined && filtered_value !== null;
  return <Div flex={{
    align: 'center',
    justify: 'space-between',
    gap: 1
  }} w={100}>
      <Div flex={{
      align: 'center',
      gap: 1
    }}>
        <Tooltip delay={500} tooltip={<Text mono muted size="xs">
              {full_path}
            </Text>} side="top">
          <Text mono muted size="xs" style={{
          cursor: 'grab',
          pointerEvents: 'none'
        }}>
            {insertPathEllipsis(full_path, (width - (is_filtered ? 45 : 20)) / 8)}
          </Text>
        </Tooltip>
        {is_filtered && <Div flex={{
        align: 'center',
        justify: 'center'
      }} style={{
        cursor: 'grab',
        pointerEvents: 'none'
      }}>
            <Tooltip side="top" delay={500} element={<Div style={{
          maxHeight: '300px',
          width: '300px',
          overflow: 'auto'
        }} p={{
          x: 1
        }}>
                  <Text size="xs" semi muted m={{
            b: 1
          }}>
                    Filter value
                  </Text>
                  <JSONBrowser json={{
            [`${path[path.length - 1]}`]: filtered_value
          }} compact />
                </Div>}>
              <Icon icon="filter_list" />
            </Tooltip>
          </Div>}
      </Div>
      <Button ghost icon="close" small onClick={e => {
      e.stopPropagation();
      table_context.removeDynamicColumn(full_path);
    }} />
    </Div>;
};
const StyledKey = styled.span<{
  compact: boolean;
}>`
  font-size: ${({
  theme,
  compact
}) => theme.pxToRem(compact ? 12 : 13)};
  font-family:
    JetBrains Mono,
    monospace;
`;
const StyledValue = styled.span<{
  type: JSONPrimitive;
  compact: boolean;
}>(({
  theme,
  type,
  compact
}) => css`
    color: ${theme.colors.on.code[String(type)]};
    font-size: ${theme.pxToRem(compact ? 12 : 13)};
    font-family:
      JetBrains Mono,
      monospace;
    display: inline-block;
    max-width: 100%;
    word-break: break-all;
  `);
const StyledHint = styled.span<{
  count?: number;
  disable_selection?: boolean;
  closed?: boolean;
  compact: boolean;
}>(({
  theme,
  count,
  disable_selection,
  closed,
  compact
}) => css`
    color: ${theme.colors.on.neutral.primary_neutral};
    margin: 0;
    border: none;
    padding: ${theme.spacing(0.25)};
    background-color: transparent;
    font-size: ${theme.pxToRem(compact ? 12 : 13)};

    &:focus-visible {
      outline: 2px solid ${theme.colors.outline.focus.primary};
      outline-offset: 1px;
    }

    ${disable_selection ? css`
          user-select: none;
        ` : css`
          padding: ${theme.spacing(0.25)};
          border-radius: ${theme.radius.small};
          &:hover {
            background-color: ${theme.colors.surface.base.variant_surface};
          }
        `}
    ${count !== undefined && css`
      &::after {
        content: ${count === 0 ? '"Empty"' : `"${count} ${pluralize(count, 'item')} ${closed ? '↓' : '↑'}"`};
        display: inline-block;
        user-select: none;
        margin-left: ${theme.spacing(1)};
        font-size: ${theme.pxToRem(compact ? 11 : 12)};
        color: ${theme.colors.on.neutral.secondary_neutral};
        font-family:
          JetBrains Mono,
          monospace;
      }
    `}
  `);
const StyledCollapseButton = styled(Button)<{
  compact: boolean;
}>(({
  theme,
  compact
}) => css`
    position: absolute;
    top: 0;
    right: 0;

    color: ${theme.colors.on.neutral.primary_neutral};
    border: none;
    background-color: transparent;
    font-size: ${theme.pxToRem(compact ? 11 : 12)};
    padding: ${theme.spacing(0.5)} ${theme.spacing(2)};
    margin: ${theme.spacing(-0.5)} ${theme.spacing(-2)};
    font-weight: normal;

    &:focus {
      outline: none;
      background-color: transparent;
    }

    &:focus-visible {
      box-shadow: none;
      outline: 2px solid ${theme.colors.outline.focus.primary};
      outline-offset: 1px;
    }
  `);
const StyledLine = styled.span<{
  depth: number;
}>(({
  theme,
  depth
}) => css`
    display: block;
    padding-left: ${theme.spacing(0.5 * depth + 2 as any)};
    position: relative;
    background-color: transparent;
    border: none;
    outline: none;
    text-align: left;
    overflow-wrap: anywhere;
  `);
const StyledLineContentHover = styled.span<{
  active: boolean;
}>(({
  theme,
  active
}) => css`
    position: relative;
    display: inline-block;
    border-radius: ${theme.radius.small};
    &:hover {
      background-color: ${theme.colors.surface.base.variant_surface};
    }
    ${active && css`
      background-color: ${theme.colors.surface.base.variant_surface};
    `}
    cursor: pointer;
  `);
const StyledKeyContentHover = styled.span<{
  active: boolean;
}>(({
  theme,
  active
}) => css`
    position: relative;
    display: inline;
    border-radius: ${theme.radius.small};
    &:hover {
      background-color: ${theme.colors.surface.base.variant_surface};
    }
    ${active && css`
      background-color: ${theme.colors.surface.base.variant_surface};
    `}
    cursor: pointer;
  `);
const setNestedValue = (obj: any, path: (string | number)[], value: any): any => {
  if (path.length === 0) return value;
  if (path.length === 1) {
    if (Array.isArray(obj)) {
      const new_array = [...obj];
      new_array[path[0] as number] = value;
      return new_array;
    }
    return {
      ...obj,
      [path[0]]: value
    };
  }
  const [first, ...rest] = path;
  const next_key = rest[0];
  const should_be_array = typeof next_key === 'number';
  if (Array.isArray(obj)) {
    const new_array = [...obj];
    const current_value = new_array[first as number];
    new_array[first as number] = setNestedValue(current_value !== undefined ? current_value : should_be_array ? [] : {}, rest, value);
    return new_array;
  }
  const current_value = obj[first];
  return {
    ...obj,
    [first]: setNestedValue(current_value !== undefined ? current_value : should_be_array ? [] : {}, rest, value)
  };
};
const removeNestedValue = (obj: any, path: (string | number)[]): any => {
  if (path.length === 0) return obj;
  if (path.length === 1) {
    if (Array.isArray(obj)) {
      return obj.filter((_, i) => i !== path[0]);
    }
    const {
      [path[0]]: _,
      ...rest
    } = obj;
    return rest;
  }
  const [first, ...rest] = path;
  if (!obj[first]) return obj;
  const updated_nested = removeNestedValue(obj[first], rest);
  const is_empty = Array.isArray(updated_nested) ? updated_nested.length === 0 : Object.keys(updated_nested).length === 0;
  if (is_empty) {
    if (Array.isArray(obj)) {
      return obj.filter((_, i) => i !== first);
    }
    const {
      [first]: _,
      ...remaining
    } = obj;
    return remaining;
  }
  if (Array.isArray(obj)) {
    const new_array = [...obj];
    new_array[first as number] = updated_nested;
    return new_array;
  }
  return {
    ...obj,
    [first]: updated_nested
  };
};
const getNestedValue = (obj: any, path: (string | number)[]): any => {
  if (path.length === 0) return obj;
  const [first, ...rest] = path;
  if (!obj || typeof obj !== 'object') return undefined;
  return getNestedValue(obj[first], rest);
};
export const useCodeMenuOptions = (json_key: string, json_value: JSONValue, config: ContextMenuConfig | null, path: (string | number)[], appended_header?: boolean) => {
  const copyToClipboard = useCopyToClipboard();
  const filters_context = useContext(FiltersContext);
  const table_context = useContext(TableContext);
  const {
    addToast
  } = useToasts();
  const [filter_added, setFilterAdded] = useState(false);
  const [table_added, setTableAdded] = useState(false);
  const filter_field_key = config?.filter_field_key;
  const table_column_prefix = config?.table_column_prefix;
  useEffect(() => {
    try {
      if (appended_header || !filter_field_key) return;
      const current_body = JSON.parse(filters_context.values?.[filter_field_key] || '{}');
      const normalized_path = path.map(p => typeof p === 'number' ? 0 : p);
      const existing_value = getNestedValue(current_body, normalized_path);
      setFilterAdded(JSON.stringify(existing_value) === JSON.stringify(json_value));
    } catch (error) {
      setFilterAdded(false);
    }
  }, [filters_context.values, filter_field_key, path, json_value, appended_header]);
  useEffect(() => {
    if (appended_header || !table_column_prefix) {
      setTableAdded(false);
      return;
    }
    const field_path = `${table_column_prefix}.${path.map(String).join('.')}`;
    // Check if any column matches this field_path (ignoring width)
    const is_active = table_context.dynamic_columns.some(col => {
      const col_field_path = col.split(':')[0];
      return col_field_path === field_path;
    });
    setTableAdded(is_active);
  }, [table_context.dynamic_columns, path, table_column_prefix, appended_header]);
  const addToFilter = () => {
    if (appended_header || !filter_field_key) return;
    const current_filter = JSON.parse(filters_context.values?.[filter_field_key] || '{}');
    // Normalize array indices to 0 so the filter doesn't create sparse arrays with nulls
    const normalized_path_0 = path.map(p_0 => typeof p_0 === 'number' ? 0 : p_0);
    const updated_filter = setNestedValue(current_filter, normalized_path_0, json_value);
    filters_context.formik.setFieldValue(filter_field_key, JSON.stringify(updated_filter, null, 2));
    filters_context.formik.handleSubmit();
    addToast('success', 'Added to filters');
  };
  const removeFromFilter = () => {
    if (appended_header || !filter_field_key) return;
    const current_body_0 = JSON.parse(filters_context.values?.[filter_field_key] || '{}');
    const normalized_path_1 = path.map(p_1 => typeof p_1 === 'number' ? 0 : p_1);
    const updated_body = removeNestedValue(current_body_0, normalized_path_1);
    filters_context.formik.setFieldValue(filter_field_key, JSON.stringify(updated_body, null, 2));
    filters_context.formik.handleSubmit();
    addToast('success', 'Removed from filters');
  };
  const addFieldToTable = () => {
    if (!table_column_prefix) return;
    const field_path_0 = `${table_column_prefix}.${path.map(String).join('.')}`;

    // Calculate width based on the value
    let width = 200; // default
    if (json_value !== undefined && json_value !== null) {
      let value_string: string;
      if (typeof json_value === 'object') {
        value_string = JSON.stringify(json_value);
      } else {
        value_string = String(json_value);
      }
      // 8px per character, min 150px, max 600px
      const calculated_width = value_string.length * 8;
      width = Math.max(150, Math.min(600, calculated_width));
    }
    const column_string = `${field_path_0}:${width}`;
    table_context.addDynamicColumn(column_string);
    addToast('success', 'Added to table');
  };
  const removeFromTable = () => {
    if (!table_column_prefix) return;
    const field_path_1 = `${table_column_prefix}.${path.map(String).join('.')}`;

    // removeDynamicColumn now handles matching by field path
    table_context.removeDynamicColumn(field_path_1);
    addToast('success', 'Removed from table');
  };
  const show_filter_actions = Boolean(filter_field_key);
  const options = [...(config ? [...(show_filter_actions ? [{
    label: filter_added ? 'Remove from filter' : 'Add to filter',
    icon: filter_added ? 'cancel' : 'filter_list',
    onClick: () => filter_added ? removeFromFilter() : addToFilter(),
    selected: filter_added,
    disabled: appended_header,
    tooltip: appended_header ? 'Appended headers cannot be added to the filter' : undefined
  }] : []), ...(table_column_prefix ? [{
    label: table_added ? 'Remove from table' : 'Add field to table',
    icon: table_added ? 'cancel' : 'add_circle',
    onClick: () => table_added ? removeFromTable() : addFieldToTable(),
    selected: table_added
  }] : [])] : []), {
    label: `Copy to clipboard`,
    icon: 'copy',
    onClick: () => copyToClipboard(`"${json_key}": ${JSON.stringify(json_value, null, 2)}`)
  }] as DropdownMenuOption[];
  return options;
};
const StyledJSONKeyWrapper = styled.span`
  display: inline-block;
  margin-right: ${({
  theme
}) => theme.spacing(2)};

  ${StyledDropdownWrapper} {
    display: inline-block;
  }
`;
const JSONKeyWrapper: React.FC<{
  json_key: string;
  json_value: JSONValue;
  context_menu: boolean;
  context_menu_config: ContextMenuConfig | null;
  path: (string | number)[];
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(6);
  const {
    json_key,
    json_value,
    context_menu,
    context_menu_config,
    path,
    children
  } = t0;
  const options = useCodeMenuOptions(json_key, json_value, context_menu ? context_menu_config : null, path);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      min: 192,
      max: 192
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = (opened, toggle) => <StyledKeyContentHover active={opened} onClick={() => toggle(true)}>{children}</StyledKeyContentHover>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== options || $[4] !== t2) {
    t3 = <StyledJSONKeyWrapper><DropdownMenu parent_width={t1} options={options} renderToggle={t2} /></StyledJSONKeyWrapper>;
    $[3] = options;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
const JSONLine: React.FC<{
  is_primitive: boolean;
  children: React.ReactNode;
  depth: number;
  json_key: string;
  json_value: JSONValue;
  context_menu: boolean;
  context_menu_config: ContextMenuConfig | null;
  path: (string | number)[];
}> = t0 => {
  const $ = _c(12);
  const {
    is_primitive,
    children,
    depth,
    json_key,
    json_value,
    context_menu,
    context_menu_config,
    path
  } = t0;
  const options = useCodeMenuOptions(json_key, json_value, context_menu ? context_menu_config : null, path);
  if (!is_primitive) {
    let t1;
    if ($[0] !== children || $[1] !== depth) {
      t1 = <StyledLine depth={depth}>{children}</StyledLine>;
      $[0] = children;
      $[1] = depth;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      min: 208,
      max: 208
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== children) {
    t2 = (opened, toggle) => <StyledLineContentHover active={opened} onClick={() => toggle(true)}>{children}</StyledLineContentHover>;
    $[4] = children;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== options || $[7] !== t2) {
    t3 = <DropdownMenu parent_width={t1} options={options} renderToggle={t2} />;
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== depth || $[10] !== t3) {
    t4 = <StyledLine depth={depth}>{t3}</StyledLine>;
    $[9] = depth;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
const HighlightSearchTerm: React.FC<{
  Component: any;
  value: string;
  compact: boolean;
  search_term: string;
  props?: object;
}> = t0 => {
  const $ = _c(21);
  const {
    Component,
    value,
    search_term,
    props,
    compact
  } = t0;
  const theme = useTheme();
  if (!search_term) {
    let t1;
    if ($[0] !== props) {
      t1 = props || {};
      $[0] = props;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== Component || $[3] !== t1 || $[4] !== value) {
      t2 = <Component {...t1}>{value}</Component>;
      $[2] = Component;
      $[3] = t1;
      $[4] = value;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t1;
  if ($[6] !== Component || $[7] !== compact || $[8] !== props || $[9] !== search_term || $[10] !== theme || $[11] !== value) {
    const parts = splitOnSearchTerm(value, search_term);
    let t2;
    if ($[13] !== Component || $[14] !== compact || $[15] !== props || $[16] !== search_term || $[17] !== theme) {
      t2 = (p, i) => <Component {...props || {}} compact={compact} key={i} style={search_term === p ? {
        backgroundColor: theme.colors.surface.container.highlight
      } : {}}>{p}</Component>;
      $[13] = Component;
      $[14] = compact;
      $[15] = props;
      $[16] = search_term;
      $[17] = theme;
      $[18] = t2;
    } else {
      t2 = $[18];
    }
    t1 = parts.map(t2);
    $[6] = Component;
    $[7] = compact;
    $[8] = props;
    $[9] = search_term;
    $[10] = theme;
    $[11] = value;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[19] !== t1) {
    t2 = <>{t1}</>;
    $[19] = t1;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  return t2;
};
const PrimitiveComponent: React.FC<{
  value: JSONPrimitive;
  search_term: string;
  compact: boolean;
}> = t0 => {
  const $ = _c(7);
  const {
    value,
    search_term,
    compact
  } = t0;
  let type = "string";
  if (typeof value === "number") {
    type = "number";
  } else {
    if (typeof value === "boolean") {
      type = "boolean";
    } else {
      if (value === null) {
        type = "null";
      }
    }
  }
  let t1;
  if ($[0] !== type) {
    t1 = {
      type
    };
    $[0] = type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = type === "string" ? `"${value}"` : String(value);
  let t3;
  if ($[2] !== compact || $[3] !== search_term || $[4] !== t1 || $[5] !== t2) {
    t3 = <HighlightSearchTerm compact={compact} search_term={search_term} Component={StyledValue} props={t1} value={t2} />;
    $[2] = compact;
    $[3] = search_term;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
type CollapseStatus = 'collapsed' | 'expanded' | null;
const RecursiveJsonComponent: React.FC<{
  search_term: string;
  search_results: boolean | object | undefined;
  json: JSONValue;
  depth: number;
  compact: boolean;
  context_menu: boolean;
  context_menu_config: ContextMenuConfig | null;
  collapse_status: CollapseStatus;
  setCollapse: (status: CollapseStatus, last_action: 'collapse' | 'expand') => void;
  path?: (string | number)[];
}> = t0 => {
  const $ = _c(40);
  const {
    search_results,
    search_term,
    json,
    depth,
    collapse_status,
    setCollapse,
    compact,
    context_menu,
    context_menu_config: t1,
    path: t2
  } = t0;
  const context_menu_config = t1 === undefined ? null : t1;
  let t3;
  if ($[0] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const path = t3;
  const [closed, setClosed] = useState(false);
  let t4;
  let t5;
  if ($[2] !== collapse_status || $[3] !== json) {
    t4 = () => {
      if (!isPrimitive(json)) {
        if (collapse_status === "expanded") {
          setClosed(false);
        } else {
          if (collapse_status === "collapsed") {
            setClosed(true);
          }
        }
      }
    };
    t5 = [collapse_status, json];
    $[2] = collapse_status;
    $[3] = json;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  if (isPrimitive(json)) {
    const t6 = json as JSONPrimitive;
    let t7;
    if ($[6] !== compact || $[7] !== search_term || $[8] !== t6) {
      t7 = <PrimitiveComponent search_term={search_term} value={t6} compact={compact} />;
      $[6] = compact;
      $[7] = search_term;
      $[8] = t6;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    return t7;
  }
  const is_array = Array.isArray(json);
  let t6;
  let t7;
  let t8;
  if ($[10] !== closed || $[11] !== collapse_status || $[12] !== compact || $[13] !== context_menu || $[14] !== context_menu_config || $[15] !== depth || $[16] !== is_array || $[17] !== json || $[18] !== path || $[19] !== search_results || $[20] !== search_term || $[21] !== setCollapse) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const entries = is_array ? json.map(_temp4) : Object.entries(json as {
        [key: string]: JSONValue;
      });
      if (entries.length === 0) {
        const t9 = is_array ? "[ ]" : "{ }";
        let t10;
        if ($[25] !== compact || $[26] !== t9) {
          t10 = <StyledHint compact={compact} count={0}>{t9}</StyledHint>;
          $[25] = compact;
          $[26] = t9;
          $[27] = t10;
        } else {
          t10 = $[27];
        }
        t8 = t10;
        break bb0;
      }
      let t9;
      if ($[28] !== closed || $[29] !== collapse_status || $[30] !== json || $[31] !== setCollapse) {
        t9 = e => {
          e.stopPropagation();
          if (!isPrimitive(json)) {
            setCollapse(collapse_status, !closed ? "collapse" : "expand");
            setClosed(_temp5);
          }
        };
        $[28] = closed;
        $[29] = collapse_status;
        $[30] = json;
        $[31] = setCollapse;
        $[32] = t9;
      } else {
        t9 = $[32];
      }
      const onClick = t9;
      if (closed) {
        t8 = <StyledHint as="button" disable_selection={true} compact={compact} count={entries.length} closed={true} onClick={onClick}>{is_array ? " [...]" : " {...}"}</StyledHint>;
        break bb0;
      }
      t6 = <StyledHint as="button" compact={compact} count={entries.length} {...entries.length > 0 ? {
        onClick
      } : {}}>{is_array ? "[" : "{"}</StyledHint>;
      t7 = entries.map((t10, i_0) => {
        const [key, value_0] = t10;
        if (search_results && !search_results[i_0]) {
          return null;
        }
        const current_path = [...path, key];
        const is_primitive_value = isPrimitive(value_0);
        return <JSONLine key={key} depth={depth + 1} json_key={String(key)} json_value={value_0} is_primitive={is_primitive_value} context_menu={context_menu} context_menu_config={context_menu_config} path={current_path}>{is_array ? <StyledHint compact={compact} disable_selection={true}>{key}:{" "}</StyledHint> : is_primitive_value ? <HighlightSearchTerm Component={StyledKey} compact={compact} search_term={search_term} value={`"${key}": `} /> : <JSONKeyWrapper json_key={String(key)} json_value={value_0} context_menu={context_menu} context_menu_config={context_menu_config} path={current_path}><HighlightSearchTerm Component={StyledKey} compact={compact} search_term={search_term} value={`"${key}": `} /></JSONKeyWrapper>}<RecursiveJsonComponent context_menu_config={context_menu_config} compact={compact} search_results={!search_results || !is_array && String(key).indexOf(search_term) >= 0 ? undefined : search_results[i_0]} search_term={search_term} json={value_0} depth={depth + 1} collapse_status={collapse_status} setCollapse={setCollapse} context_menu={context_menu} path={current_path} />{i_0 !== entries.length - 1 && <StyledHint compact={compact}>,</StyledHint>}</JSONLine>;
      });
    }
    $[10] = closed;
    $[11] = collapse_status;
    $[12] = compact;
    $[13] = context_menu;
    $[14] = context_menu_config;
    $[15] = depth;
    $[16] = is_array;
    $[17] = json;
    $[18] = path;
    $[19] = search_results;
    $[20] = search_term;
    $[21] = setCollapse;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  const t9 = is_array ? "]" : "}";
  let t10;
  if ($[33] !== compact || $[34] !== t9) {
    t10 = <StyledHint compact={compact}>{t9}</StyledHint>;
    $[33] = compact;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== t10 || $[37] !== t6 || $[38] !== t7) {
    t11 = <>{t6}{t7}{t10}</>;
    $[36] = t10;
    $[37] = t6;
    $[38] = t7;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  return t11;
};
const JSONBrowser: React.FC<{
  search_term?: string;
  json: JSONValue;
  compact?: boolean;
  context_menu?: boolean;
  context_menu_config?: ContextMenuConfig | null;
}> = t0 => {
  const $ = _c(24);
  const {
    search_term,
    json,
    compact,
    context_menu: t1,
    context_menu_config: t2
  } = t0;
  const context_menu = t1 === undefined ? false : t1;
  const context_menu_config = t2 === undefined ? null : t2;
  let t3;
  if ($[0] !== json || $[1] !== search_term) {
    t3 = search_term && search_term?.length > 1 ? buildSearchResults(search_term, json) : undefined;
    $[0] = json;
    $[1] = search_term;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const search_results = t3;
  const [collapse_status, setCollapseStatus] = useState("expanded");
  const [last_action, setLastAction] = useState(null);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (status, last_action_0) => {
      setCollapseStatus(null);
      setLastAction(last_action_0);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const setCollapse = t4;
  if (search_term && search_term?.length > 1 && !search_results) {
    let t5;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Text muted={true}>No match found.</Text>;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    return t5;
  }
  let collapse_action;
  if ($[5] !== collapse_status || $[6] !== last_action) {
    collapse_action = "collapsed";
    if (collapse_status === "collapsed" || last_action === "collapse") {
      collapse_action = "expanded";
    }
    $[5] = collapse_status;
    $[6] = last_action;
    $[7] = collapse_action;
  } else {
    collapse_action = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      position: "relative"
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = !!compact;
  const t7 = search_term && search_term?.length > 1 ? search_term : "";
  let t8;
  if ($[9] !== collapse_status || $[10] !== context_menu || $[11] !== context_menu_config || $[12] !== json || $[13] !== search_results || $[14] !== t6 || $[15] !== t7) {
    t8 = <RecursiveJsonComponent compact={t6} search_term={t7} search_results={search_results} json={json} depth={0} collapse_status={collapse_status} setCollapse={setCollapse} context_menu={context_menu} context_menu_config={context_menu_config} />;
    $[9] = collapse_status;
    $[10] = context_menu;
    $[11] = context_menu_config;
    $[12] = json;
    $[13] = search_results;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== collapse_action || $[18] !== compact || $[19] !== json) {
    t9 = !isPrimitive(json) && <StyledCollapseButton compact={!!compact} ghost={true} m={{
      t: 1
    }} onClick={() => {
      setCollapseStatus(collapse_action !== "collapsed" ? "expanded" : "collapsed");
      setLastAction(collapse_action !== "collapsed" ? "expand" : "collapse");
    }}><Text mono={true} neutral={true} size="s">{collapse_action !== "collapsed" ? "Expand all \u2193" : "Collapse all \u2191"}</Text></StyledCollapseButton>;
    $[17] = collapse_action;
    $[18] = compact;
    $[19] = json;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <Div style={t5}>{t8}{t9}</Div>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
};
export default JSONBrowser;
function _temp4(value, i) {
  return [i, value];
}
function _temp5(closed_0) {
  return !closed_0;
}